import React, { useEffect, useState } from 'react';
import { orderService, Order } from '../services/orderService';
import { useAuth } from '../context/AuthContext';
import './Orders.css';

interface DeleteModalState {
  isOpen: boolean;
  order: (Order & { combinedQuantity: number; originalOrders: Order[] }) | null;
}

export default function Orders() {
  const { user } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [filter, setFilter] = useState<'all' | 'my'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState<DeleteModalState>({ isOpen: false, order: null });
  const [deleteCount, setDeleteCount] = useState<string>('alle');

  useEffect(() => {
    loadOrders();
  }, [filter, statusFilter]);

  const loadOrders = async () => {
    setLoading(true);
    setError('');
    try {
      let data;
      if (filter === 'my') {
        data = await orderService.getMyOrders();
      } else {
        data = await orderService.getOrders(statusFilter || undefined);
      }
      // Sort by Zielort (instanceIdentifier or location)
      data.sort((a, b) => {
        const zielortA = (a.instanceIdentifier || a.location || '').toLowerCase();
        const zielortB = (b.instanceIdentifier || b.location || '').toLowerCase();
        return zielortA.localeCompare(zielortB);
      });
      setOrders(data);
    } catch (err) {
      setError('Fehler beim Laden der Bestellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId: string, newStatus: string) => {
    try {
      await orderService.updateOrderStatus(orderId, newStatus);
      // Update state directly instead of reloading
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status: newStatus as Order['status'] } : order
        )
      );
    } catch (err) {
      alert('Fehler beim Aktualisieren des Status');
    }
  };

  const handleBulkStatusChange = async (zielort: string, newStatus: string) => {
    try {
      const ordersToUpdate = orders.filter(o => 
        (o.instanceIdentifier || o.location) === zielort
      );
      
      await Promise.all(
        ordersToUpdate.map(order => 
          orderService.updateOrderStatus(order.id, newStatus)
        )
      );
      
      // Update state directly instead of reloading
      const orderIdsToUpdate = ordersToUpdate.map(o => o.id);
      setOrders(prevOrders => 
        prevOrders.map(order => 
          orderIdsToUpdate.includes(order.id) ? { ...order, status: newStatus as Order['status'] } : order
        )
      );
    } catch (err) {
      alert('Fehler beim Aktualisieren der Status');
    }
  };

  const fetchExportHtml = async (): Promise<string> => {
    const apiBase = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${apiBase}/orders/export/html`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });

    if (!response.ok) {
      throw new Error('Export failed');
    }

    return response.text();
  };

  const injectExportMeta = (html: string): string => {
    const printedBy = user?.username || 'Unbekannt';
    const printedAt = new Date().toLocaleString('de-DE');

    const metaBlock = `
<section style="margin: 0 0 16px 0; padding: 10px 12px; border: 1px solid #d9dce5; border-radius: 6px; background: #fafbff; font-family: Arial, sans-serif; color: #1f2d3d;">
  <div style="font-weight: 700; margin-bottom: 4px;">BRK KV Dachau</div>
  <div style="font-size: 13px; margin-bottom: 2px;">Automatisch generierter Ausdruck</div>
  <div style="font-size: 13px;">Gedruckt von: ${printedBy} · Datum/Zeit: ${printedAt}</div>
</section>`;

    if (html.includes('<body>')) {
      return html.replace('<body>', `<body>${metaBlock}`);
    }
    if (html.includes('<body ')) {
      return html.replace(/<body[^>]*>/, (match) => `${match}${metaBlock}`);
    }
    return `${metaBlock}${html}`;
  };

  const handleExport = async (format: 'html' | 'pdf') => {
    try {
      const htmlContentRaw = await fetchExportHtml();
      const htmlContent = injectExportMeta(htmlContentRaw);

      if (format === 'html') {
        const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'bestellungen.html';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        return;
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        throw new Error('Print window blocked');
      }

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
      }, 250);
    } catch (err) {
      alert('Fehler beim Exportieren');
    }
  };

  const cycleStatus = (currentStatus: string): string => {
    const statusCycle = ['pending', 'ordered', 'delivered'];
    const currentIndex = statusCycle.indexOf(currentStatus);
    const nextIndex = (currentIndex + 1) % statusCycle.length;
    return statusCycle[nextIndex];
  };

  const handleStatusClick = async (orderId: string, currentStatus: string) => {
    const newStatus = cycleStatus(currentStatus);
    await handleStatusChange(orderId, newStatus);
  };

  const handleCombinedStatusClick = async (
    orderGroup: Order & { combinedQuantity: number; originalOrders: Order[] }
  ) => {
    const newStatus = cycleStatus(orderGroup.status);
    await Promise.all(orderGroup.originalOrders.map(o => handleStatusChange(o.id, newStatus)));
  };

  const handleDeleteOrder = async (orderId: string) => {
    try {
      await orderService.deleteOrder(orderId);
      // Update state directly instead of reloading
      setOrders(prevOrders => prevOrders.filter(order => order.id !== orderId));
    } catch (err) {
      alert('Fehler beim Löschen der Bestellung');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'status-pending';
      case 'ordered': return 'status-ordered';
      case 'delivered': return 'status-delivered';
      case 'approved': return 'status-approved';
      case 'completed': return 'status-completed';
      case 'cancelled': return 'status-cancelled';
      default: return '';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending': return 'Ausstehend';
      case 'ordered': return 'Bestellt';
      case 'delivered': return 'Geliefert';
      case 'approved': return 'Genehmigt';
      case 'completed': return 'Abgeschlossen';
      case 'cancelled': return 'Storniert';
      default: return status;
    }
  };

  return (
    <div className="orders-page">
      <div className="page-header">
        <h1>Bestellungen</h1>
        <div className="export-buttons">
          <button onClick={() => handleExport('pdf')} className="btn-export">
            Export PDF
          </button>
          <button onClick={() => handleExport('html')} className="btn-export">
            Export HTML
          </button>
        </div>
      </div>

      <div className="filters">
        <div className="filter-group">
          <button 
            className={filter === 'all' ? 'active' : ''}
            onClick={() => setFilter('all')}
          >
            Alle Bestellungen
          </button>
          <button 
            className={filter === 'my' ? 'active' : ''}
            onClick={() => setFilter('my')}
          >
            Meine Bestellungen
          </button>
        </div>

        <div className="filter-group">
          <label>Status:</label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">Alle</option>
            <option value="pending">Ausstehend</option>
            <option value="approved">Genehmigt</option>
            <option value="completed">Abgeschlossen</option>
            <option value="cancelled">Storniert</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : (
        <div className="orders-list">
          {orders.length === 0 ? (
            <p className="no-orders">Keine Bestellungen gefunden</p>
          ) : (
            <>
              {/* Group orders by Zielort and combine same items */}
              {Object.entries(
                orders.reduce((groups, order) => {
                  const zielort = order.instanceIdentifier || order.location || 'Unbekannt';
                  if (!groups[zielort]) groups[zielort] = [];
                  groups[zielort].push(order);
                  return groups;
                }, {} as Record<string, Order[]>)
              ).map(([zielort, ordersInGroup]) => {
                // Combine orders with same item, compartment, status, and requester
                const combinedOrders = ordersInGroup.reduce((acc, order) => {
                  const requesterKey = order.requestedBy || order.requester?.username || 'unknown';
                  const key = `${order.itemReference.name}|${order.itemReference.compartment || ''}|${order.status}|${requesterKey}`;
                  if (!acc[key]) {
                    acc[key] = {
                      ...order,
                      combinedQuantity: order.quantity,
                      originalOrders: [order]
                    };
                  } else {
                    acc[key].combinedQuantity += order.quantity;
                    acc[key].originalOrders.push(order);
                  }
                  return acc;
                }, {} as Record<string, Order & { combinedQuantity: number; originalOrders: Order[] }>);

                return (
                <div key={zielort} className="location-group">
                  <div className="location-header-wrapper">
                    <h3 className="location-header">{zielort}</h3>
                    <div className="bulk-actions">
                      <label>Alle ändern: </label>
                      <select 
                        onChange={(e) => {
                          if (e.target.value) {
                            handleBulkStatusChange(zielort, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="">-- Status wählen --</option>
                        <option value="pending">Ausstehend</option>
                        <option value="ordered">Bestellt</option>
                        <option value="delivered">Geliefert</option>
                        <option value="completed">Abgeschlossen</option>
                      </select>
                    </div>
                  </div>
                  <table className="orders-table">
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th>Menge</th>
                        <th>Status</th>
                        <th>Bestellt von</th>
                        <th>Datum</th>
                        <th>Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(combinedOrders).map(order => (
                        <tr key={order.id}>
                          <td>
                            <strong>{order.itemReference.name}</strong>
                            {order.itemReference.compartment && (
                              <div className="compartment-info">Fach: {order.itemReference.compartment}</div>
                            )}
                          </td>
                          <td>{order.combinedQuantity}</td>
                          <td>
                            <button
                              className={`status-badge clickable ${getStatusBadgeClass(order.status)}`}
                              onClick={() => handleCombinedStatusClick(order)}
                              title="Klicken um Status zu ändern"
                            >
                              {getStatusLabel(order.status)}
                            </button>
                          </td>
                          <td>{order.requester?.username || 'Unbekannt'}</td>
                          <td>{new Date(order.requestDate).toLocaleDateString('de-DE')}</td>
                          <td className="actions">
                            <button 
                              className="btn-delete"
                              onClick={() => {
                                  if (order.combinedQuantity === 1 && order.originalOrders.length === 1) {
                                    // Single item from single order - show simple confirmation
                                    if (window.confirm('Bestellung wirklich löschen?')) {
                                      handleDeleteOrder(order.originalOrders[0].id);
                                    }
                                  } else {
                                    // Multiple items or multiple orders - show modal
                                    setDeleteModal({ isOpen: true, order });
                                    setDeleteCount('alle');
                                  }
                                }}
                              >
                                Löschen
                              </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
            </>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {deleteModal.isOpen && deleteModal.order && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ isOpen: false, order: null })}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Bestellung löschen</h3>
            <p>
              Es gibt <strong>{deleteModal.order.combinedQuantity}</strong> Einheiten für diesen Artikel.
            </p>
            <p>Wie viele möchten Sie löschen?</p>
            
            <div className="delete-modal-options">
              <label>
                <input
                  type="radio"
                  name="deleteCount"
                  value="alle"
                  checked={deleteCount === 'alle'}
                  onChange={(e) => setDeleteCount(e.target.value)}
                />
                Alle löschen
              </label>
              <label>
                <input
                  type="radio"
                  name="deleteCount"
                  value="custom"
                  checked={deleteCount !== 'alle'}
                  onChange={(e) => setDeleteCount('1')}
                />
                Anzahl wählen:
                <input
                  type="number"
                  min="1"
                  max={deleteModal.order.combinedQuantity}
                  value={deleteCount === 'alle' ? '1' : deleteCount}
                  onChange={(e) => setDeleteCount(e.target.value)}
                  disabled={deleteCount === 'alle'}
                  className="delete-count-input"
                />
              </label>
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteModal({ isOpen: false, order: null })}
              >
                Abbrechen
              </button>
              <button
                className="btn-delete"
                onClick={async () => {
                  const order = deleteModal.order;
                  if (!order) return;

                  try {
                    if (deleteCount === 'alle') {
                      // Delete all orders completely
                      for (const o of order.originalOrders) {
                        await handleDeleteOrder(o.id);
                      }
                    } else {
                      const numToDelete = parseInt(deleteCount);
                      if (isNaN(numToDelete) || numToDelete < 1 || numToDelete > order.combinedQuantity) {
                        alert(`Bitte geben Sie eine Zahl zwischen 1 und ${order.combinedQuantity} ein.`);
                        return;
                      }

                      let remainingToDelete = numToDelete;
                      
                      // Process orders one by one
                      for (const o of order.originalOrders) {
                        if (remainingToDelete <= 0) break;
                        
                        if (o.quantity <= remainingToDelete) {
                          // Delete entire order
                          await handleDeleteOrder(o.id);
                          remainingToDelete -= o.quantity;
                        } else {
                          // Reduce quantity
                          const newQuantity = o.quantity - remainingToDelete;
                          await orderService.updateOrderQuantity(o.id, newQuantity);
                          // Update state
                          setOrders(prevOrders => 
                            prevOrders.map(ord => 
                              ord.id === o.id ? { ...ord, quantity: newQuantity } : ord
                            )
                          );
                          remainingToDelete = 0;
                        }
                      }
                    }
                    setDeleteModal({ isOpen: false, order: null });
                  } catch (err) {
                    alert('Fehler beim Löschen');
                  }
                }}
              >
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
